import { define } from 'custom-elements';

/**
 * The case page's purchase switch.
 *
 * The page above the switch — gallery, title, price — is the case product
 * either way. Only what sits below the switch changes: case mode keeps the
 * case's own colour picker and buy button, kit mode replaces them with the
 * `kit-builder` section, the same builder the starter kit uses, so both ways of
 * buying a case live on one URL and neither is a cut-down copy of the other.
 *
 * The builder is marked up with `data-purchase-view` and toggled with the
 * `hidden` attribute rather than a class, so the view that is not showing
 * leaves the accessibility tree as well as the screen. The mode also lands on
 * <html> as `data-purchase-mode`, which is what hides the case's own blocks
 * below the switch. The switch is drawn once, in the product section, and stays
 * in view in both modes; any further copy would be kept in step here, so
 * neither section has to know about the other.
 *
 * The starter kit is the default; the case on its own is `?mode=case`. The
 * block sets the mode inline before the page is drawn, so the kit view is
 * there from the first paint.
 */
class PurchaseMode extends HTMLElement {
  static PARAM = 'mode';
  static DEFAULT = 'kit';

  connectedCallback() {
    this.options = [...this.querySelectorAll('[data-purchase-mode-option]')];

    this.options.forEach((option) => {
      option.addEventListener('click', () => PurchaseMode.select(option.dataset.purchaseModeOption));
    });

    // One element does the URL work for the page; a second copy of the switch
    // must not double up the history entries or the popstate handling.
    if (PurchaseMode.owner) return;
    PurchaseMode.owner = this;

    this.onPopState = () => PurchaseMode.applyFromUrl();
    window.addEventListener('popstate', this.onPopState);
    PurchaseMode.applyFromUrl();
    PurchaseMode.watch();

    // The case picture on both cards follows the colour, whichever picker
    // changed it: the case's own, the dots on the photo, or the kit's.
    this.caseImages = JSON.parse(this.dataset.caseImages || '{}');
    this.onChange = (event) => PurchaseMode.showColour(this.caseImages, event.target.value);
    document.addEventListener('change', this.onChange);
  }

  /** Swaps the case picture on every copy of the switch to this colour's photo. */
  static showColour(images, colour) {
    const flat = (text) => String(text || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const match = Object.keys(images).find((name) => flat(name) === flat(colour));
    if (!match) return;

    document.querySelectorAll('[data-switch-case-image]').forEach((image) => {
      image.removeAttribute('srcset');
      image.src = images[match];
    });
  }

  disconnectedCallback() {
    if (PurchaseMode.owner !== this) return;
    window.removeEventListener('popstate', this.onPopState);
    document.removeEventListener('change', this.onChange);
    PurchaseMode.observer?.disconnect();
    PurchaseMode.observer = null;
    PurchaseMode.owner = null;
  }

  static applyFromUrl() {
    const wanted = new URLSearchParams(window.location.search).get(PurchaseMode.PARAM);
    PurchaseMode.select(wanted === 'case' ? 'case' : PurchaseMode.DEFAULT, { push: false });
  }

  static select(mode, { push = true } = {}) {
    document.documentElement.dataset.purchaseMode = mode;

    document.querySelectorAll('[data-purchase-view]').forEach((view) => {
      view.hidden = view.dataset.purchaseView !== mode;
    });

    document.querySelectorAll('[data-purchase-mode-option]').forEach((option) => {
      const on = option.dataset.purchaseModeOption === mode;
      option.classList.toggle('is-active', on);
      option.setAttribute('aria-pressed', String(on));
    });

    // After the views have been shown and hidden: the builder cannot be
    // measured while it is still hidden.
    PurchaseMode.align();

    if (push) PurchaseMode.syncUrl(mode);
  }

  /**
   * Pulls the builder up under the switch.
   *
   * The builder is a section of its own, sitting after the product section, so
   * in kit mode it starts below the whole product — which on desktop means
   * below the gallery, a column the information column is much shorter than.
   * The distance is the gallery's overhang, which changes with the viewport and
   * again as the photos load, so it is measured here and handed to CSS as
   * `--kit-pull` rather than guessed at in a stylesheet.
   *
   * Measuring is done with the pull already cleared, so repeated calls settle
   * on the same number instead of creeping.
   *
   * On desktop the gallery stays in view while the page scrolls, but only as
   * far as its own section reaches, and the steps belong to the next section.
   * So the information column is first stretched to reach the end of the
   * steps (`--kit-reach`: the switch's depth in the column plus the steps'
   * height), which lets the photos ride alongside them. Neither number depends
   * on the stretch, so it settles in one pass.
   */
  static align() {
    const kit = document.querySelector('.kit[data-purchase-view="kit"]');
    const trigger = document.querySelector('.product purchase-mode');
    if (!kit || !trigger) return;
    const product = trigger.closest('.product');
    // The block's edge rather than the switch's: on desktop the block keeps the
    // switch's bottom margin inside it, and the steps should clear that too.
    const anchor = trigger.closest('.product-block') || trigger;

    if (document.documentElement.dataset.purchaseMode !== 'kit' || kit.hidden) {
      kit.style.removeProperty('--kit-pull');
      product.style.removeProperty('--kit-reach');
      return;
    }

    const info = product.querySelector('.product__info');
    const steps = kit.querySelector('.kit__layout');
    if (info && steps) {
      const depth = anchor.getBoundingClientRect().bottom - info.getBoundingClientRect().top;
      product.style.setProperty('--kit-reach', `${Math.round(depth + steps.getBoundingClientRect().height)}px`);
    }

    kit.style.setProperty('--kit-pull', '0px');

    // Flush with the bottom of the switch. The first step brings its own space
    // below that, which is the same step spacing the case view uses, so the two
    // views open at an identical distance under the switch.
    const pull = Math.round(
      kit.getBoundingClientRect().top - anchor.getBoundingClientRect().bottom
    );

    if (pull > 0) kit.style.setProperty('--kit-pull', `${pull}px`);
  }

  /**
   * The overhang changes whenever the product section does — a photo finishing
   * loading, a font landing, the window resizing — so it is watched rather than
   * measured once. Observing the product's layout and not the builder keeps
   * this out of a loop: the pull moves the builder, never the section above it.
   * The steps are watched too, as their height sets the reach; the reach
   * resizes the section above, never the steps.
   *
   * So is the form the switch sits in. On desktop the gallery is usually the
   * taller column, so opening the description's Read more pushes the switch
   * down without the layout changing height at all; watching only the layout,
   * the steps stayed where they were and the switch slid underneath them. In
   * kit mode everything in the form below the switch is hidden, so its height
   * is exactly the depth of the switch, and nothing here resizes it.
   */
  static watch() {
    const layout = document.querySelector('.product:has(purchase-mode) .product__layout');
    if (!layout || PurchaseMode.observer) return;

    PurchaseMode.observer = new ResizeObserver(() => PurchaseMode.align());
    PurchaseMode.observer.observe(layout);
    const form = document.querySelector('.product purchase-mode')?.closest('.product__form');
    if (form) PurchaseMode.observer.observe(form);
    const steps = document.querySelector('.kit .kit__layout');
    if (steps) PurchaseMode.observer.observe(steps);
  }

  /** Kit mode clears the parameter so the default view keeps one address. */
  static syncUrl(mode) {
    const url = new URL(window.location.href);

    if (mode === 'case') url.searchParams.set(PurchaseMode.PARAM, 'case');
    else url.searchParams.delete(PurchaseMode.PARAM);

    if (url.href !== window.location.href) window.history.pushState({ url: url.href }, '', url.href);
  }
}

define('purchase-mode', PurchaseMode);
